'use client';

import { useCallback, useEffect, useState } from 'react';
import { reportError, isChunkLoadError } from '@/lib/errorReportingService';
import { isCapacitor } from '@/lib/platformUtils';
import { clearNativeCache } from '@/lib/nativeCacheBridge';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

/**
 * Global error boundary — rendered by Next.js when the root layout itself
 * throws. This file MUST render its own <html> and <body> tags because the
 * root layout is not rendered in this case.
 *
 * Uses the shared `reportError` service (same as ErrorBoundary.tsx and
 * error.tsx) instead of inline fetch logic. Includes expandable technical
 * details for better debugging.
 *
 * Chunk loading errors (deployment mismatches) are handled specially:
 * the app clears caches and auto-reloads instead of showing an error page.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [reportReference, setReportReference] = useState<string | null>(null);
  const [isReporting, setIsReporting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const isChunkError = isChunkLoadError(error);

  const handleAutoReport = useCallback(async () => {
    // Skip reporting for chunk errors — they are deployment mismatches,
    // not actionable bugs. reportError also filters them, but skipping
    // here avoids the fallback localStorage write too.
    if (isChunkLoadError(error)) return;

    setIsReporting(true);
    try {
      const result = await reportError(
        error,
        'GlobalError',
        'Critical application error - global error boundary triggered',
        {
          digest: error.digest,
          errorName: error.name,
          isCritical: true,
        },
      );

      if (result.success && result.referenceNumber) {
        setReportReference(result.referenceNumber);
      }
    } catch (err) {
      console.error('Failed to auto-report critical error:', err);
    } finally {
      setIsReporting(false);
    }
  }, [error]);

  useEffect(() => {
    console.error('CRITICAL APPLICATION ERROR:', {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      name: error.name,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      timestamp: new Date().toISOString(),
    });

    // For chunk errors, auto-recover by clearing caches and reloading
    if (isChunkLoadError(error)) {
      console.warn('[GlobalError] Chunk load error detected — auto-recovering...');
      const recover = async () => {
        try {
          if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHES' });
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
              cacheNames
                .filter(name =>
                  name.includes('nextjs') ||
                  name.includes('totalaccess') ||
                  name.includes('pages') ||
                  name.includes('navigation') ||
                  name.includes('chunks')
                )
                .map(name => caches.delete(name))
            );
          }
          if (isCapacitor()) {
            clearNativeCache();
          }
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (e) {
          console.error('[GlobalError] Cache clear failed during chunk error recovery:', e);
        } finally {
          window.location.reload();
        }
      };
      void recover();
      return;
    }

    void handleAutoReport();
  }, [error, handleAutoReport]);

  // For chunk errors, show a branded "Updating..." UI while recovering
  if (isChunkError) {
    // Detect dark mode: the ThemeContext applies the `dark` class to
    // documentElement. Since global-error renders its own <html>, we
    // mirror that class so BrandedLoadingSpinner's dark: Tailwind styles work.
    const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
    return (
      <html className={isDark ? 'dark' : undefined}>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Updating - TotalAccess</title>
        </head>
        <body style={{ margin: 0, padding: 0 }}>
          <div className="fixed inset-0 flex items-center justify-center bg-white dark:bg-gray-900">
            <BrandedLoadingSpinner
              size="lg"
              message="Updating App..."
              subMessage="A new version is available. Clearing cache and reloading..."
              showSurface
              showProgressBar
              suppressClearCacheButton
            />
          </div>
        </body>
      </html>
    );
  }

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Critical Error - TotalAccess</title>
      </head>
      <body style={{ margin: 0, padding: 0, fontFamily: 'system-ui, sans-serif' }}>
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          background: 'linear-gradient(135deg, #fecaca 0%, #fed7aa 100%)',
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '600px',
            width: '100%',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
              padding: '32px',
              color: 'white',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>⚠️</div>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>
                Critical Error
              </h1>
              <p style={{ fontSize: '14px', marginTop: '8px', opacity: 0.9 }}>
                The application encountered a critical error
              </p>
            </div>

            {/* Content */}
            <div style={{ padding: '32px' }}>
              {reportReference ? (
                <div style={{
                  background: '#f0fdf4',
                  border: '2px solid #86efac',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  textAlign: 'left',
                }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#166534', marginBottom: '8px' }}>
                    ✓ Error Reported
                  </div>
                  <p style={{ fontSize: '14px', color: '#15803d', marginBottom: '12px' }}>
                    Our development team has been notified and will investigate this issue.
                  </p>
                  <div style={{
                    background: 'white',
                    border: '1px solid #86efac',
                    borderRadius: '8px',
                    padding: '12px',
                  }}>
                    <div style={{ fontSize: '11px', color: '#15803d', fontWeight: 'bold', marginBottom: '4px' }}>
                      REFERENCE NUMBER
                    </div>
                    <code style={{ fontSize: '18px', fontWeight: 'bold', color: '#166534', fontFamily: 'monospace' }}>
                      {reportReference}
                    </code>
                  </div>
                </div>
              ) : isReporting ? (
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
                  Reporting error to our development team...
                </p>
              ) : (
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
                  {error.message || 'Please try reloading the application.'}
                </p>
              )}

              {error.digest && (
                <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '24px', fontFamily: 'monospace' }}>
                  Error ID: {error.digest}
                </p>
              )}

              {/* Expandable technical details */}
              {showDetails && (
                <details style={{ marginBottom: '24px', textAlign: 'left' }} open>
                  <summary style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>
                    Technical Details
                  </summary>
                  <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', fontSize: '12px' }}>
                    {error.stack && (
                      <div>
                        <strong style={{ color: '#4b5563' }}>Stack:</strong>
                        <pre style={{ marginTop: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#374151', maxHeight: '320px', overflow: 'auto', fontSize: '12px' }}>
                          {error.stack}
                        </pre>
                      </div>
                    )}
                    {!error.stack && (
                      <p style={{ color: '#6b7280' }}>No stack trace was available for this error.</p>
                    )}
                  </div>
                </details>
              )}

              <button
                onClick={() => setShowDetails(!showDetails)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '12px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  marginBottom: '24px',
                  display: 'block',
                }}
              >
                {showDetails ? 'Hide' : 'Show'} technical details
              </button>

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={reset}
                  style={{
                    padding: '14px 24px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                >
                  🔄 Reload Application
                </button>
                <button
                  onClick={handleReload}
                  style={{
                    padding: '14px 24px',
                    background: '#4b5563',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#374151'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#4b5563'}
                >
                  ↻ Force Reload
                </button>
                <button
                  onClick={handleGoHome}
                  style={{
                    padding: '14px 24px',
                    background: 'white',
                    color: '#374151',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  🏠 Go to Home Page
                </button>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px',
              background: '#f9fafb',
              borderTop: '1px solid #e5e7eb',
              fontSize: '12px',
              color: '#6b7280',
            }}>
              Error occurred at {new Date().toLocaleString()}
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
